import React, { FC, useContext, memo, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import Sections from 'components/sections/Sections';
import TimelineSection from 'components/sections/TimelineSection';
import CreateSectionButton from 'components/buttons/CreateSectionButton';
import ColorfulSectionWrapper from 'components/events/ColorfulSectionWrapper';
import TimelineSectionsHeader from 'components/sections/TimelineSectionsHeader';
import CollapseExpandSectionsButton from 'components/buttons/CollapseExpandSectionsButton';
import { SectionsFilterableContext } from '../SectionsFilterContext';

import { useModal } from 'vibo-ui/Modal';
import { hasSectionsFilterValue } from 'services/sections/helpers';
import { getNextSectionId } from 'components/buttons/NextSectionButton/constants';

import {
  REORDER_TEMPLATE_SECTIONS,
  DELETE_TEMPLATE_SECTION,
} from 'components/../graphql/mutations/template';
import { TEMPLATE_SECTIONS } from 'components/../graphql/queries/template';

import { Modals } from 'types/enums';
import { TemplateSectionsProps } from './interfaces';
import { ConfirmActionModalProps } from 'components/modals/ConfirmActionModal';
import { DeleteSectionModalProps } from 'components/modals/DeleteSectionModal/interfaces';

const TemplateSections: FC<TemplateSectionsProps> = ({
  template,
  sections,
  selectedSection,
  refetch,
  isSectionsLoading,
  onSectionClick,
  onEditClick,
}) => {
  const { t } = useTranslation();

  const templateId: string = template._id;

  const { filter, setFilter } = useContext(SectionsFilterableContext);

  const { openModal } = useModal();

  const openDeleteSectonModal = (section: TemplateSection) =>
    openModal<DeleteSectionModalProps>({
      key: Modals.deleteSection,
      props: {
        delteSectionMutation: DELETE_TEMPLATE_SECTION,
        refetchQueries: ['templateSections'],
        variables: {
          templateId,
          sectionId: section._id,
        },
        onDelete: () => {
          const nextSectionId = getNextSectionId(sections, section);

          onSectionClick(sections.find(el => el._id === nextSectionId) as EventSection);
        },
      },
    });
  const openConfirmReorderingModal = () =>
    openModal<ConfirmActionModalProps>({
      key: Modals.confirmAction,
      props: {
        title: t('clearSearch'),
        children: t('clearSearchReorderSections'),
        submit: {
          text: t('clear'),
          onClick: handleClearReordering,
        },
      },
    });

  const [reorderSections] = useMutation(REORDER_TEMPLATE_SECTIONS, {
    refetchQueries: ['templateSections'],
  });

  const handleClearReordering = useCallback(() => {
    setFilter(null);
    refetch({ templateId, filter });
  }, [templateId, filter?.q]);

  return (
    <Sections<TemplateSection, TemplateSectionsVariables>
      sections={sections}
      reorderProps={{
        // TODO handle types issue and remove ts-ignore
        // @ts-ignore
        onReorder: reorderSections,
        variables: { templateId },
        listQuery: TEMPLATE_SECTIONS,
        listCacheKey: 'templateSections',
        onBeforeCapture: () => {
          if (hasSectionsFilterValue(filter)) {
            return openConfirmReorderingModal();
          }
        },
      }}
      renderSection={({ section, ...props }): JSX.Element => (
        <ColorfulSectionWrapper headlineColors={section.headlineColors}>
          <TimelineSection
            section={section}
            isSelected={section._id === selectedSection?._id}
            onClick={onSectionClick}
            onEditClick={onEditClick}
            onDeleteClick={openDeleteSectonModal}
            {...props}
          />
        </ColorfulSectionWrapper>
      )}
      renderHeader={() => (
        <TimelineSectionsHeader
          numSections={sections.length}
          onSearch={q => {
            setFilter(prev => ({ ...prev, q }));
          }}
          collapseBtn={
            !!sections.length ? (
              <CollapseExpandSectionsButton key="expand-collapse-sections-btn" />
            ) : null
          }
          buttons={[
            template.canEdit ? (
              <CreateSectionButton
                eventSettings={template.settings}
                tooltipProps={{
                  overlay: t('addSection'),
                  fillOverlay: true,
                  placement: 'top',
                }}
                shape="round"
                size="lg"
                key="add-section-btn"
              />
            ) : null,
          ].filter(Boolean)}
        />
      )}
      isSectionsLoading={isSectionsLoading}
    />
  );
};

export default memo(TemplateSections);
